import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { formatDate } from "@/lib/format";
import EventImage from "@/components/EventImage";
import MoonPhase from "@/components/MoonPhase";
import { Flame, ArrowRight, Headphones, ExternalLink } from "lucide-react";

const SECTIONS = [
  { key: "echo", label: "Les Échos", desc: "Actualités courtes : épisodes, sorties, nouvelles des artistes & artisans." },
  { key: "podcast", label: "Podcasts", desc: "Voix et récits de la culture païenne." },
  { key: "article", label: "Articles", desc: "Analyses et grands formats." },
  { key: "rencontre", label: "Rencontres", desc: "Portraits & interviews de celles et ceux qui transmettent." },
  { key: "reportage", label: "Reportages", desc: "Immersions sur le terrain." },
  { key: "culture", label: "Culture & traditions", desc: "Comprendre les racines et les fêtes." },
  { key: "media", label: "Médias & créateurs", desc: "Podcasts, photographes, vidéastes, auteurs, chaînes." },
];

const externalCta = (section) =>
  section === "podcast" ? { label: "Écouter", Icon: Headphones }
    : section === "media" ? { label: "Découvrir", Icon: ExternalLink }
      : { label: "Visiter le site", Icon: ExternalLink };

const Card = ({ a }) => {
  const external = (a.section === "podcast" || a.section === "media") && a.external_url;
  const inner = (
    <div className="group bg-[#121418] border border-[#272A34] hover:border-amber-500/50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 h-full">
      <div className="relative h-44 overflow-hidden">
        <EventImage src={a.cover} alt={a.title} imgClassName="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] to-transparent" />
        {a.partner && <span className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full text-[10px] font-mono uppercase bg-amber-950/70 text-amber-300 border border-amber-800/40">Partenaire</span>}
      </div>
      <div className="p-5">
        <div className="font-mono text-xs text-stone-500 mb-2">{a.published_at ? formatDate(a.published_at) : ""}{a.read_minutes ? ` · ${a.read_minutes} min` : ""}</div>
        <h3 className="font-serif text-lg text-stone-100 leading-snug mb-2 line-clamp-2 group-hover:text-amber-100">{a.title}</h3>
        <p className="text-sm text-stone-400 line-clamp-2">{a.excerpt}</p>
        <div className="mt-4 flex items-center gap-1.5 text-amber-500 text-xs font-mono uppercase">
          {external ? <><Headphones className="w-3.5 h-3.5" /> Écouter <ExternalLink className="w-3 h-3" /></> : <ArrowRight className="w-3.5 h-3.5" />}
        </div>
      </div>
    </div>
  );
  return external
    ? <a href={a.external_url} target="_blank" rel="noopener noreferrer" data-testid={`campfire-card-${a.slug}`} className="block h-full">{inner}</a>
    : <Link to={`/campfire/${a.slug}`} data-testid={`campfire-card-${a.slug}`} className="block h-full">{inner}</Link>;
};

export default function Campfire() {
  const { data: articles = [], isLoading } = useQuery({ queryKey: ["campfire"], queryFn: async () => (await api.get("/articles")).data });
  const featured = articles.find((a) => a.featured) || articles[0];
  const bySection = (k) => articles.filter((a) => a.section === k && a.slug !== featured?.slug);

  return (
    <>
      <Seo title="CAMPFIRE — Le média de la culture païenne en Suisse" description="Actualités, podcasts, rencontres, reportages et traditions de la culture païenne, viking et médiévale en Suisse." canonicalPath="/campfire" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="mb-10 border-b border-amber-900/30 pb-6">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2 flex items-center gap-2"><Flame className="w-4 h-4" /> CAMPFIRE</div>
          <h1 className="font-serif text-4xl sm:text-6xl uppercase tracking-tight text-amber-100">Campfire</h1>
          <p className="text-stone-400 mt-3 max-w-2xl text-lg">Le média de la culture païenne en Suisse.</p>
        </div>

        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(3)].map((_, i) => <div key={i} className="h-80 bg-[#121418] border border-[#272A34] rounded-lg animate-pulse" />)}</div>
        ) : (
          <>
            {/* À la une */}
            {featured && (
              <section className="mb-14" data-testid="campfire-featured">
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-amber-500/80 mb-4">À la une</div>
                <div className="grid lg:grid-cols-2 gap-6 bg-[#121418] border border-amber-900/30 rounded-xl overflow-hidden">
                  <Link to={`/campfire/${featured.slug}`} className="group relative h-64 lg:h-full min-h-[240px] overflow-hidden block">
                    <EventImage src={featured.cover} alt={featured.title} imgClassName="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </Link>
                  <div className="p-7 flex flex-col justify-center">
                    <div className="font-mono text-xs text-stone-500 mb-3">{featured.published_at ? formatDate(featured.published_at) : ""}{featured.partner ? " · Partenaire" : ""}</div>
                    <Link to={`/campfire/${featured.slug}`} className="group"><h2 className="font-serif text-3xl text-amber-100 leading-tight mb-3 group-hover:text-amber-200">{featured.title}</h2></Link>
                    <p className="text-stone-400 line-clamp-3">{featured.excerpt}</p>
                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      {featured.external_url && (() => { const c = externalCta(featured.section); return (
                        <a href={featured.external_url} target="_blank" rel="noopener noreferrer" data-testid="campfire-featured-external" className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-4 py-2 rounded-md text-sm transition-colors">
                          <c.Icon className="w-4 h-4" /> {c.label} <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      ); })()}
                      <Link to={`/campfire/${featured.slug}`} data-testid="campfire-featured-read" className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-300 text-sm font-mono uppercase">Lire <ArrowRight className="w-4 h-4" /></Link>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {SECTIONS.map((s) => {
              const items = bySection(s.key);
              const moon = s.key === "article" ? <MoonPhase inline key="moon" /> : null;
              if (!items.length) return moon;
              return (
                <React.Fragment key={s.key}>
                  {moon}
                  <section className="mb-14" data-testid={`campfire-section-${s.key}`}>
                    <div className="flex items-end justify-between gap-4 mb-5 border-b border-[#272A34] pb-3">
                      <div>
                        <h2 className="font-serif text-2xl text-stone-100 flex items-center gap-2">{s.key === "podcast" && <Headphones className="w-5 h-5 text-amber-500" />}{s.label}</h2>
                        <p className="text-sm text-stone-500 mt-1">{s.desc}</p>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {items.map((a) => <Card key={a.slug} a={a} />)}
                    </div>
                  </section>
                </React.Fragment>
              );
            })}

            {articles.length === 0 && <div className="text-center text-stone-500 py-16" data-testid="campfire-empty">Le feu s'allume bientôt. Premiers contenus à venir.</div>}
          </>
        )}
      </div>
    </>
  );
}
